import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faClockRotateLeft,
  faEllipsisVertical,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { chaseTableActions, enhancedChaseStatusData } from "./chaseListData";
import { useMessageModel } from "../../store/use-message-model";
import React, { useRef, useState } from "react";
import { useChaseMenu } from "../../store/use-chase-menu";
import ChaseDropMenu from "./ChaseDropMenu";
import { Menu } from "@headlessui/react";
import DropdownPanel from "../DropdownPanel";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import DeleteModel from "../../models/delete-model";
import { useDeleteModel } from "../../store/use-delete-model";
import EditChaseModel from "../../models/chase-edit-model";
import { useChaseEditModel } from "../../store/use-chase-edit-model";
import moment from "moment";
import { IStatus } from "../../types/chase-item-types";
import { useSuretyModel } from "../../store/use-surety-popup";
import PopupSuretyModel from "../../models/popup-surety";
import { useNavigate } from "react-router-dom";

const iconButton =
  "cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200";

const createdFromLabels = {
  M: { label: "Manual", title: "Created manually" },
  A: { label: "Auto", title: "Created automatically" },
};

interface TChaseRenderitem {
  item: any;
  index: number;
}

interface LastMessageT {
  id: string;
  isRead: boolean;
  type: "portal" | "dashboard";
}

const getPartiallySubmitted = ({
  chaseItems = [],
  drivers = [],
  status,
}: {
  chaseItems: Record<string, any>[];
  drivers: Record<string, any>[];
  status: IStatus;
}): "Awaiting-Submitted" | "Pending-Submitted" | undefined => {
  let newItems = [...chaseItems];

  for (let i = 0; i < drivers.length; i++) {
    const currentDriver = drivers[i];

    newItems = [...newItems, ...currentDriver?.chaseItem];
  }

  return status === "Pending-Review" &&
    newItems?.find((chaseItm) => chaseItm?.status === "Awaiting-Customer") &&
    newItems?.find((chaseItm) => chaseItm?.status === "Pending-Review")
    ? "Pending-Submitted"
    : status === "Awaiting-Customer" &&
        newItems?.find(
          (chaseItm) => chaseItm?.status === "Awaiting-Customer",
        ) &&
        newItems?.find((chaseItm) => chaseItm?.status === "Approved")
      ? "Awaiting-Submitted"
      : undefined;
};

const getStatus = (status: string) => {
  const statusData = enhancedChaseStatusData.find(
    (stat) => stat.name === (status || "default"),
  );
  return statusData;
};

const ChaseRenderitem: React.FC<TChaseRenderitem> = ({
  item: itemData,
  index,
}) => {
  const navigate = useNavigate();
  const [item, setItem] = useState<Record<string, any> | null>(itemData);
  const [lastMessage, setLastMessage] = useState<LastMessageT | null>(
    item?.messages[0],
  );
  const [unReadMessages, setUnreadMessages] = useState<number>(
    item?.unReadMessageCount,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { onOpen } = useMessageModel();
  const { initialValues, onToggleChaseMenu, onChaseMenuOpen } = useChaseMenu();
  const {
    initialValues: deletePopupInitialValues,
    onOpenDeletePopup,
    onCloseDeletePopup,
  } = useDeleteModel();
  const {
    initialValues: suretyPopupInitialValues,
    onOpenSuretyPopup,
    onCloseSuretyPopup,
  } = useSuretyModel();
  const { initialValues: editInitialValues, onOpen: onEditOpen } =
    useChaseEditModel();
  const lastDate = new Date(item?.startDate);
  lastDate.setDate(lastDate.getDate() + 14);
  const isPartiallySubmitted = getPartiallySubmitted({
    chaseItems: item?.chaseItem,
    drivers: item?.drivers,
    status: item?.status,
  });
  const isExpanded = !!item?.id && initialValues?.id === item.id;
  const customerName = `${item?.firstName} ${item?.surname}`;

  // --------------------------- [ DELETE CHASE ] ------------------------------
  const HandleDeleteChase = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.delete(`/chase/${item?.id}`);
      onCloseDeletePopup();
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // --------------------------- [ HANDLE ON EDIT CALL ] ------------------------------

  const HandleEditChase = () => {
    onEditOpen({ id: item?.id, data: item });
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const UpdateItemData = async () => {
    try {
      const res = await axiosInstance.get(`/chase?id=${item?.id}`);
      const resData = res.data?.data[0];
      onChaseMenuOpen({
        id: resData?.id,
        chaseItems: resData?.chaseItem,
        drivers: resData?.drivers,
        data: {
          policyReferenceNumber: resData?.policyReferenceNumber,
          apiValues: resData.apiValues,
        },
        timeline: resData?.timeline,
      });
      setItem(resData);
    } catch (error) {
      console.log(error);
    }
  };

  // ------------------------------- [ RESTART CHASE ] ------------------------------

  const HandleRestartChase = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/chase/restart/${item?.id}`);
      toast.success(res.data.message);
      UpdateItemData();
      onCloseSuretyPopup();
      if (res.data) {
        let rejectedMessage =
          "💡Please message customer to notify them that chase is restarting.";
        const defaultMessage =
          "Hello, we haven’t received the requested information from you in time. Fortunately, we’ve been able to extend your chase by a few extra days. Please kindly submit the information promptly. Many thanks";
        onOpen(
          item?.id?.toString(),
          item.firstName,
          rejectedMessage,
          defaultMessage,
        );
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  if (!item) {
    return null;
  }

  return (
    <>
      <tr
        className="relative font-medium border-b border-edge-faint hover:bg-overlay-hover transition-colors duration-200"
        key={index}
      >
        <td className="w-[16%] py-3.5 pl-1 align-top">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="text-[14px] font-semibold text-text-primary">
              {item?.firstName + " " + item.surname}
            </p>
            {createdFromLabels[item?.createdFrom] ? (
              <span
                className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-light-gray border border-edge rounded-full px-2 py-[3px]"
                title={createdFromLabels[item.createdFrom].title}
              >
                {createdFromLabels[item.createdFrom].label}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.policyReferenceNumber}
          </p>
        </td>

        <td className="w-[18%] py-3.5 align-top">
          <p className="text-[13px] text-text-primary break-all">
            {item?.email}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray tabular-nums">
            {item?.phone}
          </p>
        </td>

        <td className="w-[9%] py-3.5 align-top">
          <p className="text-[13px] text-text-primary">{item?.chaseType}</p>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.policyType}
          </p>
        </td>

        <td className="w-[9%] py-3.5 align-center">
          <p className="text-[13px] text-text-primary capitalize">
            {item?.insurer?.name.split(" ")[0]}
          </p>
        </td>

        <td className="w-[12%] py-3.5 align-top">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {moment(item?.startDate).format("DD MMM YYYY")}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray whitespace-nowrap">
            Ends {moment(lastDate).format("DD MMM YYYY")}
          </p>
        </td>

        <td className="w-[8%] py-3.5 align-middle">
          <p className="text-[13px] text-text-primary">{item?.driver}</p>
        </td>

        <td className="w-[11%] py-3.5 align-middle">
          {(() => {
            const status = getStatus(isPartiallySubmitted || item?.status);
            return (
              <div
                className={clsx(
                  "inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full",
                  status?.bgColor,
                )}
              >
                {status?.icon ? (
                  <FontAwesomeIcon
                    icon={status.icon}
                    className={clsx("text-[11px]", status?.color)}
                  />
                ) : null}
                <p
                  className={clsx(
                    "text-[11px] font-semibold whitespace-nowrap",
                    status?.color,
                  )}
                >
                  {item?.status.split("-")[0]}
                </p>
              </div>
            );
          })()}
        </td>

        <td className="w-[6%] py-3.5 align-middle">
          <div className="flex items-center justify-center">
            <button
              type="button"
              title="Chase logs"
              className={iconButton}
              onClick={() => {
                window.open(
                  `/chaselogs/${item?.policyReferenceNumber}`,
                  "_blank",
                );
              }}
            >
              <FontAwesomeIcon
                icon={faClockRotateLeft}
                className="text-[13px]"
              />
            </button>
          </div>
        </td>

        <td className="py-3.5 align-middle" colSpan={2}>
          <div className="flex items-center justify-end gap-0.5">
            <button
              type="button"
              title="Notes & messages"
              className={clsx(iconButton, "relative")}
              onClick={() => {
                onOpen(item?.id, item?.firstName);
                setUnreadMessages(0);
                setLastMessage(null);
              }}
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-[15px]" />
              {unReadMessages ||
              (lastMessage?.type === "portal" && lastMessage?.isRead) ? (
                <span
                  className={clsx(
                    "absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full text-[9px] text-white flex justify-center items-center font-bold tabular-nums bg-gradient-to-r from-accent to-accent-bright shadow-glow-sm",
                    lastMessage?.type === "portal" &&
                      lastMessage?.isRead &&
                      "!bg-none !bg-emerald-500",
                  )}
                >
                  {lastMessage?.type === "portal" && lastMessage?.isRead
                    ? 1
                    : unReadMessages}
                </span>
              ) : null}
            </button>

            <Menu as="div" className="relative inline-block text-left">
              {({ open }) => (
                <>
                  <Menu.Button
                    ref={menuButtonRef}
                    title="More actions"
                    className={iconButton}
                  >
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                      className="text-[15px]"
                    />
                  </Menu.Button>

                  <DropdownPanel open={open} anchorRef={menuButtonRef} align="right">
                    <Menu.Items
                      static
                      className="min-w-[170px] p-1 rounded-xl bg-surface-elevated border border-edge shadow-overlay focus:outline-none"
                    >
                      {chaseTableActions.map((action, index) => (
                        <Menu.Item key={index}>
                          <div
                            className={clsx(
                              "flex gap-2.5 px-3 py-2 rounded-lg text-[13px] items-center font-medium cursor-pointer transition-colors duration-200",
                              action.name === "Delete Chase"
                                ? "text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                                : "text-text-primary hover:bg-overlay-hover",
                            )}
                            onClick={() => {
                              action.name === "Edit Chase"
                                ? HandleEditChase()
                                : action.name === "Restart Chase"
                                  ? onOpenSuretyPopup({
                                      id: item?.id,
                                      onClick: HandleRestartChase,
                                      tone: "primary",
                                      title: "Restart this chase?",
                                      description:
                                        "The timeline resets to day one and the status goes back to awaiting customer. You'll be prompted to message the customer afterwards.",
                                      detail: `${customerName} · ${item?.policyReferenceNumber}`,
                                      confirmLabel: "Restart chase",
                                    })
                                  : onOpenDeletePopup({
                                      id: item.id,
                                      onClick: HandleDeleteChase,
                                      scope: "chase",
                                      tone: "danger",
                                      title: "Delete this chase?",
                                      description:
                                        "The chase, its chase items, uploaded documents and message history are all removed. This can't be undone.",
                                      detail: `${customerName} · ${item?.policyReferenceNumber}`,
                                      confirmLabel: "Delete chase",
                                    });
                            }}
                          >
                            <FontAwesomeIcon
                              icon={action?.icon}
                              className="text-[12px]"
                            />
                            {action.name}
                          </div>
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </DropdownPanel>
                </>
              )}
            </Menu>

            <button
              type="button"
              title={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              onClick={() =>
                onToggleChaseMenu({
                  id: item?.id,
                  chaseItems: item?.chaseItem,
                  drivers: item?.drivers,
                  data: {
                    policyReferenceNumber: item?.policyReferenceNumber,
                    apiValues: item?.apiValues,
                  },
                  timeline: item?.timeline,
                })
              }
              className={iconButton}
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  "text-[13px] transition-transform duration-200",
                  isExpanded && "rotate-180",
                )}
              />
            </button>
          </div>
        </td>
      </tr>
      <tr>
        {initialValues?.id === item?.id && (
          <ChaseDropMenu UpdateItemData={UpdateItemData} />
        )}
      </tr>
      <tr>
        <td>
          {deletePopupInitialValues?.id === item?.id &&
            deletePopupInitialValues?.scope === "chase" && (
              <DeleteModel loading={loading} />
            )}
        </td>
      </tr>
      <tr>
        <td>
          {suretyPopupInitialValues?.id === item?.id && (
            <PopupSuretyModel loading={loading} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {editInitialValues.id === item?.id && (
            <EditChaseModel UpdateItemData={UpdateItemData} />
          )}
        </td>
      </tr>
    </>
  );
};

export default ChaseRenderitem;
